import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiMessageSquare,
  FiSend,
  FiChevronDown,
  FiUsers,
  FiX,
} from "react-icons/fi";

/**
 * A collapsible comment thread for a single release / backlog item.
 *
 * Presentational + local UI state only. Persistence is delegated to the parent
 * via `onAdd`, which talks to releaseNotesAPI.
 *
 * Every comment is directed at a named person (the `audiences` list comes from
 * AdminDetails). There is deliberately no "Everyone" option: it read as though
 * the whole team would be notified, when the mail actually goes to the
 * reviewers and the people already in the thread. Naming a recipient makes the
 * dropdown mean what it says.
 */
const MAX_COMMENT_LENGTH = 1000;

const initials = (name) =>
  (name || "U")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("") || "U";

const formatWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const CommentThread = ({
  itemId,
  comments = [],
  currentUser,
  audiences = [],
  onAdd,
  // True when an emailed link named this item. The thread opens itself and
  // scrolls into view, so "View the discussion" lands on the discussion rather
  // than on a page where it is one collapsed row among many.
  isLinked = false,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const [text, setText] = useState("");
  // Ids (AdminID), not names — names in AdminDetails aren't unique, and the
  // backend needs an unambiguous key to work out whose mailbox to notify.
  // A comment can be directed at several people; every one of them is
  // notified. Empty until at least one is chosen.
  const [shareTargets, setShareTargets] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pickerRef = useRef(null);

  const selectedAudiences = useMemo(
    () =>
      shareTargets
        .map((id) => audiences.find((a) => String(a.id) === String(id)))
        .filter(Boolean),
    [shareTargets, audiences]
  );

  const toggleTarget = (id) =>
    setShareTargets((prev) =>
      prev.some((x) => String(x) === String(id))
        ? prev.filter((x) => String(x) !== String(id))
        : [...prev, id]
    );

  // Close the picker on an outside click. Without this it stays open over the
  // comment box and hides what you are typing.
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDocClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target))
        setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [pickerOpen]);

  const sorted = useMemo(
    () =>
      [...comments].sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      ),
    [comments]
  );

  useEffect(() => {
    if (!isLinked) return;
    setOpen(true);
    // Deferred a frame so the thread has expanded before we measure where to
    // scroll to; otherwise it centres on the collapsed height.
    const id = window.requestAnimationFrame(() =>
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    );
    return () => window.cancelAnimationFrame(id);
  }, [isLinked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    // At least one recipient is required — the notification has nowhere to go
    // without one.
    if (!trimmed || selectedAudiences.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      await onAdd({
        itemId,
        text: trimmed.slice(0, MAX_COMMENT_LENGTH),
        // Names are what get stored and displayed; the ids are what the
        // backend resolves mailboxes from. Everyone listed is notified.
        sharedWith: selectedAudiences.map((a) => a.name).join(", "),
        sharedWithIds: selectedAudiences.map((a) => a.id),
        author: currentUser?.name || "User",
        role: currentUser?.role || "",
      });
      setText("");
      setShareTargets([]);
      setPickerOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const count = comments.length;

  return (
    <div
      className={`rn-thread ${isLinked ? "is-linked" : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`rn-thread-toggle ${open ? "is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        <FiMessageSquare size={15} />
        <span>
          {count > 0
            ? `${count} comment${count === 1 ? "" : "s"}`
            : "Add a comment"}
        </span>
        <FiChevronDown className="rn-thread-chevron" size={15} />
      </button>

      {open && (
        <div className="rn-thread-body">
          {sorted.length > 0 && (
            <ul className="rn-comment-list">
              {sorted.map((c) => (
                <li key={c.id} className="rn-comment">
                  <div className="rn-comment-avatar">{initials(c.author)}</div>
                  <div className="rn-comment-main">
                    <div className="rn-comment-head">
                      <span className="rn-comment-author">
                        {c.author || "User"}
                        {c.role ? (
                          <span className="rn-comment-role"> · {c.role}</span>
                        ) : null}
                      </span>
                      <span className="rn-comment-when">
                        {formatWhen(c.createdAt)}
                      </span>
                    </div>
                    <div className="rn-comment-text">{c.text}</div>
                    {/* Comments written before the "Everyone" option was
                        removed still carry it, so they keep rendering with
                        their original label rather than looking broken. */}
                    {c.sharedWith ? (
                      <span
                        className={`rn-comment-audience ${
                          c.sharedWith === "Everyone" ? "is-everyone" : ""
                        }`}
                      >
                        <FiUsers size={11} /> {c.sharedWith}
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <form className="rn-comment-form" onSubmit={handleSubmit}>
            <textarea
              className="rn-comment-input"
              placeholder="Leave a comment, ask a question, or redirect a priority…"
              value={text}
              onChange={(e) =>
                setText(e.target.value.slice(0, MAX_COMMENT_LENGTH))
              }
              maxLength={MAX_COMMENT_LENGTH}
              rows={2}
            />
            <div
              className={`rn-comment-count ${
                text.length >= MAX_COMMENT_LENGTH ? "is-max" : ""
              }`}
            >
              {text.length}/{MAX_COMMENT_LENGTH}
            </div>
            <div className="rn-comment-actions">
              {/* A <select> allows one choice only, which is why a second
                  person could never be added. Replaced with a checkbox list:
                  picking someone no longer replaces whoever was already
                  chosen, and everybody listed is notified. */}
              <div className="rn-share" ref={pickerRef}>
                <button
                  type="button"
                  className="rn-share-trigger"
                  onClick={() => setPickerOpen((v) => !v)}
                  aria-expanded={pickerOpen}
                  aria-haspopup="true"
                >
                  <FiUsers size={13} />
                  {selectedAudiences.length === 0
                    ? "Share with…"
                    : selectedAudiences.length === 1
                      ? selectedAudiences[0].name
                      : `${selectedAudiences.length} people`}
                  <FiChevronDown size={13} />
                </button>

                {pickerOpen && (
                  <div className="rn-share-menu" role="group" aria-label="Share with">
                    {audiences.length === 0 && (
                      <div className="rn-share-empty">No one to choose from</div>
                    )}
                    {audiences.map((a) => {
                      const id = a.id ?? a.name;
                      const checked = shareTargets.some(
                        (x) => String(x) === String(id)
                      );
                      return (
                        <label key={id} className="rn-share-option">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTarget(id)}
                          />
                          <span className="rn-share-option-name">
                            {a.name}
                            {a.role ? (
                              <span className="rn-share-option-role">
                                {" "}
                                · {a.role}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* The chips are outside the menu so the choices stay visible
                  once it closes — otherwise you'd have to reopen it to check
                  who you had picked. */}
              {selectedAudiences.length > 0 && (
                <div className="rn-share-chips">
                  {selectedAudiences.map((a) => (
                    <span key={a.id ?? a.name} className="rn-share-chip">
                      {a.name}
                      <button
                        type="button"
                        onClick={() => toggleTarget(a.id ?? a.name)}
                        aria-label={`Remove ${a.name}`}
                      >
                        <FiX size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <button
                type="submit"
                className="rn-comment-submit"
                disabled={
                  !text.trim() || selectedAudiences.length === 0 || submitting
                }
                title={
                  selectedAudiences.length === 0
                    ? "Choose who this comment is for"
                    : undefined
                }
              >
                <FiSend size={14} />
                {submitting ? "Posting…" : "Post"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CommentThread;
